/**
 * POIFieldExtractor - Shared POI field-value extraction (DUP-16)
 *
 * Pure functions moved out of POITableContext so any module can extract
 * field values from raw API POI rows (supports both IFC and RVT data
 * structures stored in custom_data). POITableContext re-exports these
 * for backward compatibility.
 *
 * Note: getPOISourceFile here is the POI-table variant (also checks
 * rvt_source_file and customData.originalFileName, no fallback param) —
 * intentionally distinct from utils/POIFileUtils.getPOISourceFile.
 */

// Parse custom_data JSON field
export const parseCustomData = (poi: any): any => {
  if (!poi?.custom_data) return null;
  try {
    return typeof poi.custom_data === 'string' ? JSON.parse(poi.custom_data) : poi.custom_data;
  } catch {
    return null;
  }
};

// Derive an equipment subcategory from the IFC ObjectType stored in custom_data
// (E-12c): THE single derivation — LocationsPage and SystemsPage previously used
// different regex chains, so the same POI landed in differently-named subcategory
// groups on the two pages. Canonical chain = the stricter SystemsPage cleanup
// (strip " N:...", trailing digits, and wrapping quotes), applied for both.
// Returns null when no usable ObjectType exists (callers own their fallbacks).
export const deriveSubcategoryFromObjectType = (poi: any): string | null => {
  const objectType = parseCustomData(poi)?.ifc?.ifcProperties?.ObjectType;
  if (!objectType || typeof objectType !== 'string') return null;
  const cleaned = objectType
    .replace(/\s*\d+:.*$/, '')
    .replace(/\s*\d+$/, '')
    .replace(/^"/, '')
    .replace(/"$/, '')
    .trim();
  return cleaned || null;
};

// Get Autodesk/Revit element dbId from a POI, supporting both normalized and raw API shapes.
export const getPOIElementDbId = (poi: any): number | null => {
  const customData = parseCustomData(poi);
  const rawDbId =
    customData?.rvt?.rvtElementDbId ??
    customData?.rvtElementDbId ??
    customData?.rvt?.dbId ??
    poi?.rvt_element_db_id ??
    poi?.rvt_element_dbid ??
    poi?.ifc_element_express_id ??
    poi?.properties?.ifcElementExpressId;

  if (rawDbId === null || rawDbId === undefined || rawDbId === '') {
    return null;
  }

  const dbId = typeof rawDbId === 'string' ? parseInt(rawDbId, 10) : rawDbId;
  return typeof dbId === 'number' && !Number.isNaN(dbId) ? dbId : null;
};

// Get POI source file
export const getPOISourceFile = (poi: any): string | null => {
  const customData = parseCustomData(poi);
  const sourceFile =
    poi?.ifc_source_file ||
    poi?.source_file ||
    poi?.rvt_source_file ||
    poi?.properties?.location ||
    poi?.properties?.source_file ||
    customData?.ifc?.originalFileName ||
    customData?.rvt?.originalFileName ||
    customData?.originalFileName ||
    customData?.rvt?.sourceFile;

  if (sourceFile) return sourceFile;
  return null;
};

// Get field value from POI (supports both IFC and RVT data structures)
/**
 * THE raw-row system derivation (systems are DERIVED — there is no 'system'
 * field): user-assigned Set-Systems category first, raw IFC category fallback.
 * Mirrors SystemsPage's top-level grouping (which works on CONVERTED rows via
 * properties.systemCategory || properties.originalCategory — same values).
 * Consumers on raw /pois rows must use this, not a hand-rolled chain.
 */
export const getSystemValue = (poi: any): string =>
  String(poi.system_category || poi.ifc_category || '').trim();

export const getFieldValue = (poi: any, field: string): string => {
  const customData = parseCustomData(poi);
  const rvtProps = customData?.rvt?.rvtProperties || {};
  const ifcProps = customData?.ifc || {};

  switch (field) {
    case 'name':
      // API uses 'title' for element names
      return poi.title || poi.name || '';

    case 'category':
      // For IFC: ifc_category, for RVT: poi_group_name or rvt.rvtCategory
      return poi.ifc_category ||
             poi.poi_group_name?.replace('RVT ', '') ||
             customData?.rvt?.rvtCategory ||
             '';

    case 'type':
      // For IFC: ifc_type_name, for RVT: poi_type_name or rvt.rvtTypeName
      return poi.ifc_type_name ||
             poi.poi_type_name ||
             customData?.rvt?.rvtTypeName ||
             rvtProps['Type Name'] ||
             '';

    case 'subcategory':
      return poi.subcategory ||
             customData?.rvt?.rvtType ||
             '';

    case 'room_name': {
      // Use aggregated locations array if available (for POIs in multiple rooms)
      if (poi.locations && poi.locations.length > 0) {
        return poi.locations.map((l: any) => l.name).join(', ');
      }
      // Fallback to raw location_name from API
      if (poi.location_type === 'Spaces' && poi.location_name) {
        return poi.location_name;
      }
      // Fallback to RVT properties
      return rvtProps['Room Name'] || rvtProps['Room'] || ifcProps?.containedInRoom || '';
    }

    case 'room_number':
      return rvtProps['Room Number'] ||
             rvtProps['Room #'] ||
             '';

    case 'source_file':
      // Source file works for both IFC and RVT
      return getPOISourceFile(poi) || '';

    case 'system_category':
      return poi.system_category || '';

    case 'system_subcategory':
      return poi.system_subcategory || '';

    case 'system_name':
      return poi.system_name || '';

    case 'element_id':
      // Element ID for IFC or RVT
      return getPOIElementDbId(poi)?.toString() ||
             rvtProps['ElementId'] ||
             '';

    case 'workset':
      return rvtProps['Workset'] || '';

    case 'phase':
      return rvtProps['Phase Created'] || rvtProps['Phase'] || '';

    case 'level': {
      // Use database level from aggregated locations
      if (poi.locations && poi.locations.length > 0) {
        const levels = [...new Set(poi.locations.map((l: any) => l.level_name).filter(Boolean))];
        if (levels.length > 0) {
          return levels.join(', ');
        }
      }
      // POI assigned directly to a storey: the storey itself IS the level —
      // parent_location_name would be the BUILDING. (Storey predicate inlined from
      // StoreyClassifier.isStoreyType; importing it here would create an import cycle
      // via parseCustomData.)
      const locType = String(poi.location_type || '').toLowerCase();
      if ((locType.includes('storey') || locType.includes('level') || locType === 'floor') && poi.location_name) {
        return poi.location_name;
      }
      // Fallback to raw parent_location_name from API
      if (poi.parent_location_name) {
        return poi.parent_location_name;
      }
      // Fallback to RVT properties
      return rvtProps['Level'] || rvtProps['IAA_Level'] || rvtProps['Base Level'] || '';
    }

    case 'mark':
      return rvtProps['Mark'] || '';

    case 'ifc_guid':
      return poi.ifc_element_guid ||
             rvtProps['IfcGUID'] ||
             customData?.rvt?.rvtElementGuid ||
             '';

    default:
      // Handle dynamic fields from custom_data (format: "rvt:PropertyName" or "ifc:PropertyName")
      if (field.startsWith('rvt:')) {
        const propName = field.substring(4);
        const value = rvtProps[propName];
        return value != null ? String(value) : '';
      }
      if (field.startsWith('ifc:')) {
        const propPath = field.substring(4);
        if (propPath.includes('.')) {
          // Property set path like "Data.IAA_Room_Code"
          const [setName, propName] = propPath.split('.');
          const propSets = ifcProps?.propertySets || {};
          return propSets[setName]?.[propName] != null ? String(propSets[setName][propName]) : '';
        }
        // Direct property
        const props = ifcProps?.properties || {};
        return props[propPath] != null ? String(props[propPath]) : '';
      }
      return '';
  }
};
